import React, { useState } from 'react';
import {
  Copy,
  Send,
  RefreshCw,
  X,
  Check,
  Loader2,
} from 'lucide-react';
import { schoolsApi } from '../../../lib/api';
import { useSchool } from '../../../contexts/SchoolContext';

interface StudentInvite {
  id: string;
  email: string;
  status: 'pending' | 'approved' | 'rejected';
  sentAt: string;
}

const mockInvites: StudentInvite[] = [
  { id: '1', email: 'student1@email.com', status: 'pending', sentAt: '2024-01-15' },
  { id: '2', email: 'student2@email.com', status: 'approved', sentAt: '2024-01-10' },
  { id: '3', email: 'student3@email.com', status: 'pending', sentAt: '2024-01-08' },
];

const statusColors: Record<string, string> = {
  pending: 'bg-yellow-100 text-yellow-700',
  approved: 'bg-green-100 text-green-700',
  rejected: 'bg-red-100 text-red-700',
};

export function InviteStudentsSection() {
  const { school } = useSchool();
  const [invites, setInvites] = useState<StudentInvite[]>(mockInvites);
  const [inviteEmail, setInviteEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [studentEmail, setStudentEmail] = useState('');
  const [copied, setCopied] = useState(false);
  const [sending, setSending] = useState(false);
  const [adding, setAdding] = useState(false);

  const inviteLink = school?.id
    ? `${window.location.origin}/invite/student/${school.id}`
    : 'https://viscool.edu/invite/student/xyz789';

  const handleCopyLink = () => {
    navigator.clipboard.writeText(inviteLink);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleSendInvite = async () => {
    if (!inviteEmail || !school?.id) return;

    try {
      setSending(true);
      await schoolsApi.inviteUser(String(school.id), {
        email: inviteEmail,
        role: 'student',
      });

      const newInvite: StudentInvite = {
        id: Date.now().toString(),
        email: inviteEmail,
        status: 'pending',
        sentAt: new Date().toISOString().split('T')[0],
      };
      setInvites([newInvite, ...invites]);
      setInviteEmail('');
    } catch (error) {
      console.error('Failed to send invite:', error);
      alert('Failed to send invitation. Please try again.');
    } finally {
      setSending(false);
    }
  };

  const handleAddStudent = async () => {
    if (!firstName || !lastName || !studentEmail || !school?.id) return;

    try {
      setAdding(true);
      await schoolsApi.inviteUser(String(school.id), {
        email: studentEmail,
        role: 'student',
        first_name: firstName,
        last_name: lastName,
      });

      const newInvite: StudentInvite = {
        id: Date.now().toString(),
        email: studentEmail,
        status: 'pending',
        sentAt: new Date().toISOString().split('T')[0],
      };
      setInvites([newInvite, ...invites]);
      setFirstName('');
      setLastName('');
      setStudentEmail('');
    } catch (error) {
      console.error('Failed to add student:', error);
      alert('Failed to add student. Please try again.');
    } finally {
      setAdding(false);
    }
  };

  const handleResend = async (id: string) => {
    const invite = invites.find(i => i.id === id);
    if (!invite || !school?.id) return;

    try {
      await schoolsApi.inviteUser(String(school.id), {
        email: invite.email,
        role: 'student',
      });
      alert('Invitation resent successfully!');
    } catch (error) {
      console.error('Failed to resend invite:', error);
      alert('Failed to resend invitation. Please try again.');
    }
  };

  const handleCancel = (id: string) => {
    setInvites((prev) => prev.filter((i) => i.id !== id));
  };

  return (
    <div className="space-y-6">
      {/* Invite by Link */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-2">Invite Students into your school</h2>
        <p className="text-sm text-gray-500 mb-4">
          Share the invite link with students or send them an email invitation.
        </p>

        {/* Copy Link */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            1. Send the link via your way
          </label>
          <div className="flex items-center gap-2">
            <input
              type="text"
              value={inviteLink}
              readOnly
              className="flex-1 px-4 py-2 bg-gray-50 border border-gray-300 rounded-lg text-sm text-gray-600"
            />
            <button
              onClick={handleCopyLink}
              className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                copied
                  ? 'bg-green-100 text-green-700'
                  : 'bg-teal-600 text-white hover:bg-teal-700'
              }`}
            >
              {copied ? (
                <>
                  <Check className="w-4 h-4" />
                  Copied!
                </>
              ) : (
                <>
                  <Copy className="w-4 h-4" />
                  Copy Link
                </>
              )}
            </button>
          </div>
        </div>

        {/* Send by Email */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            2. Or, by Student's mail
          </label>
          <div className="flex items-center gap-2">
            <input
              type="email"
              value={inviteEmail}
              onChange={(e) => setInviteEmail(e.target.value)}
              placeholder="You can add more than one"
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
            />
            <button
              onClick={handleSendInvite}
              disabled={sending || !inviteEmail}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-teal-600 rounded-lg hover:bg-teal-700 disabled:opacity-50"
            >
              {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              {sending ? 'Sending...' : 'Send'}
            </button>
          </div>
        </div>
      </div>

      {/* Invites Table */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <h3 className="font-semibold text-gray-900 mb-4">Manage Your Student's Invites</h3>
        <p className="text-sm text-gray-500 mb-4">
          View all the invitations you've sent and manage pending invitations.
        </p>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-200">
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Invited Email</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {invites.map((invite) => (
                <tr key={invite.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm text-gray-900">{invite.email}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-block px-2 py-1 text-xs font-medium rounded-full ${statusColors[invite.status]}`}>
                      {invite.status.charAt(0).toUpperCase() + invite.status.slice(1)}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handleResend(invite.id)}
                        className="flex items-center gap-1 px-2 py-1 text-xs text-teal-600 hover:bg-teal-50 rounded"
                      >
                        <RefreshCw className="w-3 h-3" />
                        Resend
                      </button>
                      <button
                        onClick={() => handleCancel(invite.id)}
                        className="flex items-center gap-1 px-2 py-1 text-xs text-red-600 hover:bg-red-50 rounded"
                      >
                        <X className="w-3 h-3" />
                        Cancel
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add Student Manually */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <h3 className="font-semibold text-gray-900 mb-2">Invite Students into your school</h3>
        <p className="text-sm text-gray-500 mb-4">
          From here, you can invite students to join your school
        </p>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Student's Name</label>
            <div className="grid grid-cols-2 gap-4">
              <input
                type="text"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                placeholder="First Name"
                className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
              />
              <input
                type="text"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                placeholder="Last Name"
                className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Student Mail</label>
            <input
              type="email"
              value={studentEmail}
              onChange={(e) => setStudentEmail(e.target.value)}
              placeholder="Student's Mail"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
            />
          </div>

          <p className="text-xs text-gray-500">
            After adding a student, they will be notified by mail.
          </p>

          <div className="flex items-center gap-3 pt-2">
            <button
              onClick={() => {
                setFirstName('');
                setLastName('');
                setStudentEmail('');
              }}
              className="px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Reset
            </button>
            <button
              onClick={handleAddStudent}
              disabled={adding || !firstName || !lastName || !studentEmail}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-teal-600 rounded-lg hover:bg-teal-700 disabled:opacity-50"
            >
              {adding && <Loader2 className="w-4 h-4 animate-spin" />}
              {adding ? 'Adding...' : 'Add Student'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
